import React, { useMemo } from 'react';
import { Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import Button from 'components/Button';
import FormInput from 'components/FormInput';
import Modal from 'components/Modal';
import { useHomeScreenContext } from 'layouts/HomeScreenLayout/contexts/HomeScreenContext';
import { USER_BUDGET_TEMPLATES_FOLDER_NAME } from 'utils/budgetTemplates';
import { toastStyle } from 'utils/helper';

import './styles.scss';

const SaveBudgetAsTemplate = () => {
  const {
    selectedBudget,
    budgetData,
    showSaveAsTemplate,
    hideSaveAsTemplateModal,
    createFolderMutation,
    duplicateBudgetMutation,
    foldersData,
    folderListing,
    refetchFolders,
    refetchBudgets,
  } = useHomeScreenContext();

  const budgetName = budgetData?.name || selectedBudget?.name || '';
  const defaultTemplateName = budgetName ? `${budgetName} Template` : '';

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    watch,
  } = useForm({
    defaultValues: {
      name: defaultTemplateName,
      include_values: false,
    },
  });

  const folders = useMemo(
    () => foldersData?.data || folderListing || [],
    [folderListing, foldersData]
  );

  const handleCancel = () => {
    hideSaveAsTemplateModal();
    reset({
      name: defaultTemplateName,
      include_values: false,
    });
  };

  const getTemplateFolder = async () => {
    const existingFolder = folders?.find(
      folder =>
        folder?.type === 'Folder' &&
        folder?.name === USER_BUDGET_TEMPLATES_FOLDER_NAME &&
        !folder?.parent_folder
    );

    if (existingFolder?.id) return existingFolder;

    const response = await createFolderMutation.mutateAsync({
      name: USER_BUDGET_TEMPLATES_FOLDER_NAME,
    });

    await refetchFolders?.();
    return response?.data;
  };

  const handleSaveTemplate = handleSubmit(async data => {
    if (!selectedBudget?.budget) return;

    const templateName = data?.name?.trim();
    if (!templateName) return;

    toast.loading(`Saving "${templateName}" as a template...`);
    try {
      const templateFolder = await getTemplateFolder();
      await duplicateBudgetMutation.mutateAsync({
        budget_id: selectedBudget.budget,
        withValues: Boolean(data?.include_values),
        name: templateName,
        is_user_template: true,
        copy_folders: false,
        target_folder_id: templateFolder?.id,
      });

      await refetchBudgets?.();
      await refetchFolders?.();
      toast.dismiss();
      toast.success(`Budget template "${templateName}" saved`, toastStyle);
      handleCancel();
    } catch (error) {
      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.name?.[0] ||
        error?.response?.data?.non_field_errors?.[0];
      toast.dismiss();
      toast.error(
        message || 'An error occurred while saving the budget template.',
        toastStyle
      );
    }
  });

  return (
    <Modal
      onHide={handleCancel}
      show={showSaveAsTemplate}
      size="sm"
      className="save-budget-template"
      bodyClass="save-budget-template__body"
      centered={true}
    >
      <h1 className="save-budget-template__body__header">
        Save as template
        {watch('name') && (
          <span className="template-name">&ldquo;{watch('name')}&rdquo;</span>
        )}
      </h1>
      <Form
        className="save-budget-template__body__form"
        onSubmit={handleSaveTemplate}
      >
        <FormInput
          label="Template Name"
          type="text"
          error={errors?.name?.message}
          placeholder="Please enter a template name"
          autoFocus
          {...register('name', {
            required: 'Template name is required',
            maxLength: {
              value: 250,
              message: 'Template name must be 250 characters or fewer',
            },
          })}
        />
        <Form.Check
          type="switch"
          id="save-template-include-values"
          className="save-budget-template__body__switch"
          label="Include budget values"
          {...register('include_values')}
        />
        <div className="save-budget-template__body__action">
          <Button
            label="Cancel"
            type="reset"
            className="save-budget-template__action__cancel"
            variant="red-outlined"
            onClick={handleCancel}
            disabled={isSubmitting}
          />
          <Button
            label="Save Template"
            className="save-budget-template__action__confirm"
            type="submit"
            disabled={isSubmitting}
          />
        </div>
      </Form>
    </Modal>
  );
};

export default SaveBudgetAsTemplate;
